import _ from 'lodash';
import { Modal, ModalBody } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import { Button, Menu, MenuItem, Checkbox, FormControlLabel, FormControl, Select } from '@mui/material';

import 'animate.css';
import './index.css';
import '../../static/css/animate.css';
import Sidebar from '../../components/sidebar';
import NavbarInner from '../../components/navbar-inner';
import PaymentForm from '../../components/payment-form';
import PostPayment from '../../components/post-payment';
import Notification from '../../components/notification';
import MigrationDetailModal from '../../components/migration-detail-modal';
import { getMigration, getResults, saveReportColumns, setupFullMigration, createRelations, resyncSoftData } from '../../store/actions/Migration';

const ReportLog = () => {
    const dispatch = useDispatch();
    const { migration, results, resultPages, totalRecords, stripeSecret } = useSelector(st => st['Migration']);
    const migrationKey = new URLSearchParams(window.location.search).get('key');

    const [activeSection] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedObject, setSelectedObject] = useState(null);
    const [isRecordsExtracted, setIsRecordsExtracted] = useState(false);
    const [resultsSearch, setResultsSearch] = useState('');
    const [resultsPage, setResultsPage] = useState(1);
    const [selectedObjectName, setSelectedObjectName] = useState('');
    const [selectedColumns, setSelectedColumns] = useState([]);

    const [settingsModal, setSettingsModal] = useState(false);
    const [errorsTabObject, setErrorsTabObject] = useState(null);
    const [filterType, setFilterType] = useState(null);
    const [paymentModal, setPaymentModal] = useState(false);
    const [notification, setNotification] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('unpaid');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationSeverity, setNotificationSeverity] = useState('success');
    const open = Boolean(anchorEl);
    const stripePromise = loadStripe(process['env']['REACT_APP_STRIPE_PUB_KEY']);
    const stripePISecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

    const [filters, setFilters] = useState({ custom: false, standard: false });

    useEffect(() => {
        if (migrationKey) dispatch(getMigration({ key: migrationKey, type: 'none' }));
    }, [migrationKey]);

    const sourceProvider = _.capitalize(migration['sourceProvider'] || 'Source');

    const allObjects = useMemo(() => {
        if (!migration['objects']) return [];
        const flat = [];
        Object.values(migration['objects']).forEach(row => {
            row.forEach(object => flat.push(object));
        });
        return flat;
    }, [migration['objects']]);

    const filteredObjects = useMemo(() => {
        if (filters.custom && !filters.standard) return allObjects.filter(obj => obj['custom']);
        if (filters.standard && !filters.custom) return allObjects.filter(obj => !obj['custom']);
        return allObjects;
    }, [allObjects, filters]);


    const hiddenColumns = ['attributes'];

    const availableColumns = useMemo(() => {
        if (!results || results.length === 0) return [];
        let allKeys = new Set();
        for (let r of results) {
            let d = r?.['data'] || {};
            for (let key of Object.keys(d)) {
                if (d[key] !== null && d[key] !== undefined && !hiddenColumns.includes(key)) allKeys.add(key);
            }
        }
        let cols = Array.from(allKeys);
        let idIndex = cols.findIndex(c => c === 'Id' || c === 'id');
        if (idIndex > 0) { let [id] = cols.splice(idIndex, 1); cols.unshift(id); }
        return cols;
    }, [results]);

    useEffect(() => {
        if (availableColumns.length > 0 && selectedColumns.length === 0) {
            let saved = migration['reportColumns']?.[selectedObjectName];
            if (saved && saved.length > 0) setSelectedColumns(saved.filter(c => availableColumns.includes(c)));
            else setSelectedColumns(availableColumns.slice(0, 4));
        }
    }, [availableColumns]);

    const formatCellValue = (val) => {
        if (val === null || val === undefined) return '-';
        if (Array.isArray(val)) {
            let primary = val.find(v => v?.['primary']);
            if (primary) return String(primary['value'] || primary['label'] || '-');
            if (val.length > 0 && typeof val[0] === 'object') return String(val[0]?.['value'] || val[0]?.['label'] || '-');
            return val.filter(Boolean).join(', ') || '-';
        }
        if (typeof val === 'object') return String(val['value'] || val['name'] || val['label'] || JSON.stringify(val));
        return String(val);
    };

    const formatColumnHeader = (col) => {
        if (col === 'id' || col === 'Id') return `${sourceProvider} Id`;
        return _.startCase(col);
    };


    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleChange = (key) => {
        setFilters(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const fetchResults = (objectName, search = '', page = 1, type = null) => {
        dispatch(getResults({ key: migration['key'], objectName, search, page, type }));
    };

    const handleStatClick = (_, object, type) => {
        let objName = object['key'] || object['label'];
        setSelectedObject(object);
        setSelectedObjectName(objName);
        setResultsSearch('');
        setResultsPage(1);
        setSelectedColumns([]);
        setFilterType(type);
        setIsRecordsExtracted(true);
        fetchResults(objName, '', 1, type);
    };

    const updateResultsPage = (value) => setResultsPage(parseInt(value));

    const getPageNumbers = () => {
        let total = resultPages?.length || 0;
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        let pages = [1];
        let start = Math.max(2, resultsPage - 1);
        let end = Math.min(total - 1, resultsPage + 1);
        if (resultsPage <= 3) { start = 2; end = 4; }
        if (resultsPage >= total - 2) { start = total - 3; end = total - 1; }
        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < total - 1) pages.push('...');
        pages.push(total);
        return pages;
    };

    const computeStats = (object) => {
        let insertion = object['hsObject'] || {};
        let updation = object['hsObjectUpdate'] || {};
        let extractCount = (object['noOfRecord'] && object['count'] > object['noOfRecord']) ? object['noOfRecord'] : (object['count'] || 0);
        let insertCount = (insertion['noOfRecord'] && insertion['count'] > insertion['noOfRecord']) ? insertion['noOfRecord'] : (insertion['count'] || 0);
        let updateCount = (updation['noOfRecord'] && updation['count'] > updation['noOfRecord']) ? updation['noOfRecord'] : (updation['count'] || 0);

        let recordsExtracted = Math.min(object['migratedCount'] || 0, extractCount);
        let extractedPercent = extractCount > 0 ? Math.round(recordsExtracted / extractCount * 100) : 0;

        let recordsCreated = Math.min(insertion['migratedCount'] || 0, insertCount);
        let createdPercent = insertCount > 0 ? Math.round(recordsCreated / insertCount * 100) : 0;

        let recordsUpdated = Math.min(updation['migratedCount'] || 0, updateCount);
        let updatedPercent = updateCount > 0 ? Math.round(recordsUpdated / updateCount * 100) : 0;

        // Count only unresolved errors so the table matches the migration overview
        // (resolved errors still exist in task.errors as history but shouldn't be flagged).
        const activeErrs = (errs) => (errs || []).filter(e => !e?.['resolved']).length;
        let extractionErrors = activeErrs(object['errors']);
        let insertionErrors = activeErrs(insertion['errors']) + activeErrs(updation['errors']);
        let recordsFailed = extractionErrors + insertionErrors;
        let failedPercent = extractCount > 0 ? Math.round(recordsFailed / extractCount * 100) : 0;

        return { extractCount, insertCount, updateCount, recordsExtracted, extractedPercent, recordsCreated, createdPercent, recordsUpdated, updatedPercent, recordsFailed, failedPercent, extractionErrors, insertionErrors, insertion, updation };
    };

    const getRecordName = (result) => {
        let d = result?.['data'] || {};
        return d['Name'] || d['name'] || d['title'] || d['email'] || d['primary_email']
            || (d['first_name'] || d['last_name'] ? `${d['first_name'] || ''} ${d['last_name'] || ''}`.trim() : '')
            || d['Id'] || d['id'] || '-';
    };

    const exportCSV = () => {
        if (filteredObjects.length === 0) return;
        let csvStr = `Object Name,No. Records on ${sourceProvider},No. Records on HubSpot,Records Extracted,Extracted %,Records Created,Created %,Records Updated,Updated %,Records Failed,Failed %\n`;
        for (let object of filteredObjects) {
            let stats = computeStats(object);
            let objectName = _.capitalize(object['mapping']?.['sourceName'] || object['label']);
            let recordsOnSource = object['noOfRecord'] || object['count'] || 0;
            let recordsOnHubSpot = stats.recordsCreated + stats.recordsUpdated;
            csvStr += `${objectName},${recordsOnSource},${recordsOnHubSpot},${stats.recordsExtracted},${stats.extractedPercent}%,${stats.recordsCreated},${stats.createdPercent}%,${stats.recordsUpdated},${stats.updatedPercent}%,${stats.recordsFailed},${stats.failedPercent}%\n`;
        }
        const blob = new Blob([csvStr], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${migration['companyName'] || 'migration'}_report.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className='dashboard-page report-page'>
            <Notification open={notification} setOpen={setNotification} message={notificationMessage} severity={notificationSeverity} />
            {stripePISecret &&
                <Elements stripe={stripePromise}>
                    <PostPayment
                        notificationSetter={setNotification}
                        fullMigrationSetter={setPaymentStatus}
                        notificationMessageSetter={setNotificationMessage}
                        notificationSeveritySetter={setNotificationSeverity}
                    />
                </Elements>
            }
            <Sidebar activeSection={activeSection} />
            <VisibilitySensor>
                <div className='content'>
                    <NavbarInner
                        breadcrumbs={isRecordsExtracted && selectedObject
                            ? [
                                { label: 'Dashboard', to: '/dashboard' },
                                { label: 'Report Log', onClick: () => { setIsRecordsExtracted(false); setSelectedObject(null); setFilterType(null); } },
                                { label: filterType ? `${_.capitalize(filterType)} Records` : 'Extracted Records' }
                            ]
                            : [
                                { label: 'Dashboard', to: '/dashboard' },
                                { label: 'Report Log' }
                            ]
                        }
                    />
                    <div className='dashboard-area'>

                        {isRecordsExtracted && selectedObject
                            ? <>
                                <div className='title-area'>
                                    <h2>{filterType ? `${_.capitalize(filterType)} Records` : 'Extracted Records'} {totalRecords ? <span>({totalRecords})</span> : ''}</h2>
                                    <div className='right-data-contant'>
                                        <p>{migration['type'] || ''} | {_.capitalize(migration['status'] || '')} migration
                                            <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                <div className='overlabox-tooltip'> <p>This is a {migration['status'] || ''} migration. </p> </div>
                                            </i>
                                        </p>
                                    </div>
                                </div>
                            </>
                            : <>
                                <div className='title-area'>
                                    <h2>{migration['companyName'] || 'Migration'} <span><i>|</i> ID: {migration['key']?.split('-').pop()}</span></h2>
                                    <div className='right-data-contant'>
                                        <p>{migration['type'] || ''} | {_.capitalize(migration['status'] || '')} migration
                                            <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                <div className='overlabox-tooltip'> <p>This is a {migration['status'] || ''} migration. </p> </div>
                                            </i>
                                        </p>
                                    </div>
                                </div>
                                <div className='title-area'>
                                    <div className='left-area'>
                                        <Button
                                            variant="outlined"
                                            startIcon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16"><path fill="currentColor" transform="translate(0.0875244 0)" d="M0.12187481 1.7156248C0.328125 1.2781248 0.765625 1 1.25 1L14.75 1C15.234375 1 15.671875 1.2781248 15.878125 1.7156248C16.084375 2.1531248 16.021875 2.6687498 15.715625 3.0437498L10 10.028125L10 14C10 14.378125 9.7875004 14.725 9.4468746 14.89375C9.1062489 15.0625 8.703125 15.028125 8.3999996 14.8L6.3999996 13.3C6.1468744 13.1125 5.9999995 12.815625 5.9999995 12.5L5.9999995 10.028125L0.28125 3.0406246C-0.021874905 2.6687498 -0.087500095 2.1499996 0.12187481 1.7156248Z" /></svg>}
                                            onClick={handleClick}
                                        >
                                            Filter by Object
                                            <i className='icon-arrow ml-2'>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="13">
                                                    <path fill="currentColor" d="M4.7203121 8.5296879C5.0132809 8.8226566 5.4890623 8.8226566 5.7820311 8.5296879L9.532032 4.7796879C9.8250008 4.4867191 9.8250008 4.0109372 9.532032 3.7179685C9.2390633 3.4249997 8.7632818 3.4249997 8.4703131 3.7179685L5.25 6.9382815L2.0296876 3.7203124C1.7367189 3.4273436 1.2609376 3.4273436 0.96796882 3.7203124C0.67500007 4.0132809 0.67500007 4.4890628 0.96796882 4.7820315L4.7179689 8.532032L4.7203121 8.5296879Z" />
                                                </svg>
                                            </i>
                                        </Button>

                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleClose}
                                            PaperProps={{
                                                className: "filter-report",
                                                sx: {
                                                    width: 220,
                                                    borderRadius: 2,
                                                    boxShadow: '0 10px 24px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <MenuItem disableRipple>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={filters.custom}
                                                            onChange={() => handleChange('custom')}
                                                        />
                                                    }
                                                    label="Custom Object"
                                                />
                                            </MenuItem>

                                            <MenuItem disableRipple>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={filters.standard}
                                                            onChange={() => handleChange('standard')}
                                                        />
                                                    }
                                                    label="Standard Object"
                                                />
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                    <div className='right-contant'>
                                        <button className='setting-btn' onClick={() => setSettingsModal(true)}>
                                            Settings
                                        </button>

                                        <button className='setting-btn' onClick={exportCSV}>
                                            Export
                                            <i className='icon'>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                                                    <path fill="currentColor" d="M9 1C9 0.44687462 8.5531254 0 8 0C7.4468746 0 7 0.44687462 7 1L7 8.5843754L4.7062502 6.2906256C4.3156252 5.9000006 3.6812501 5.9000006 3.2906251 6.2906256C2.9000001 6.6812506 2.9000001 7.3156257 3.2906251 7.7062507L7.2906251 11.706251C7.6812501 12.096876 8.3156252 12.096876 8.7062502 11.706251L12.70625 7.7062511C13.096875 7.3156261 13.096875 6.681251 12.70625 6.290626C12.315625 5.9000015 11.681251 5.9000015 11.290626 6.290626L9 8.5843754L9 1ZM2 11C0.8968749 11 0 11.896875 0 13L0 14C0 15.103125 0.8968749 16 2 16L14 16C15.103125 16 16 15.103125 16 14L16 13C16 11.896875 15.103125 11 14 11L10.828125 11L9.4125004 12.415625C8.6312504 13.196875 7.3656254 13.196875 6.5843754 12.415625L5.171875 11L2 11ZM13.5 12.75C13.914213 12.75 14.25 13.085787 14.25 13.5C14.25 13.914213 13.914213 14.25 13.5 14.25C13.085787 14.25 12.75 13.914213 12.75 13.5C12.75 13.085787 13.085787 12.75 13.5 12.75Z" />
                                                </svg>
                                            </i>
                                        </button>
                                    </div>
                                </div>
                            </>
                        }
                        <div id='fields-mapping' class='migration-fields-mapping mb-10'>
                            <div className="table-wrapper">
                                {isRecordsExtracted && selectedObject
                                    ? <div className='result-tabs-area'>
                                        <div className='top-form-area'>
                                            <div className='form-box'>
                                                <div className='group-form'>
                                                    <FormControl sx={{ minWidth: 250 }}>
                                                        <label className='pb-2'>Objects</label>
                                                        <Select
                                                            id='report-object-select'
                                                            value={selectedObjectName}
                                                            onChange={({ target }) => {
                                                                let newObj = allObjects.find(o => (o['key'] || o['label']) === target['value']);
                                                                setSelectedObjectName(target['value']);
                                                                setSelectedObject(newObj || selectedObject);
                                                                setResultsSearch('');
                                                                setResultsPage(1);
                                                                setSelectedColumns([]);
                                                                fetchResults(target['value'], '', 1, filterType);
                                                            }}
                                                        >
                                                            {allObjects.map(obj => (
                                                                <MenuItem key={obj['key'] || obj['label']} value={obj['key'] || obj['label']}>
                                                                    {_.capitalize(obj['mapping']?.['sourceName'] || obj['label'])}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                                <div className='group-form'>
                                                    <label className='pb-2'>Search</label>
                                                    <input type='text' value={resultsSearch} onChange={({ target }) => setResultsSearch(target['value'])} placeholder='Search by ID or Name' />
                                                </div>
                                                <button className='btn-style-one' onClick={() => { setResultsPage(1); fetchResults(selectedObjectName, resultsSearch, 1, filterType); }}>Find</button>
                                                <FormControl sx={{ minWidth: 150, maxWidth: 250 }}>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        size='small'
                                                        value={selectedColumns}
                                                        onChange={({ target }) => {
                                                            setSelectedColumns(target['value']);
                                                            dispatch(saveReportColumns({ key: migrationKey, objectName: selectedObjectName, columns: target['value'] }));
                                                        }}
                                                        renderValue={(selected) => {
                                                            if (selected.length === 0) return <span style={{ color: '#aaa' }}>Select Columns</span>;
                                                            return selected.map(c => formatColumnHeader(c)).join(', ');
                                                        }}
                                                        sx={{ fontSize: '14px', '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                                        MenuProps={{ disablePortal: false, disableScrollLock: false, PaperProps: { sx: { maxHeight: 300, overflowY: 'auto' } }, MenuListProps: { sx: { maxHeight: 300, overflowY: 'auto' } } }}
                                                    >
                                                        {availableColumns.map(col => (
                                                            <MenuItem key={col} value={col}>
                                                                <Checkbox checked={selectedColumns.includes(col)} />
                                                                {formatColumnHeader(col)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        </div>
                                        <p className={(results?.length > 0 ? 'hide' : 'display-block ml-3 pl-4')}>There are no records to show.</p>
                                        {results?.length > 0 &&
                                            <div className='table-result'>
                                                <div className='table-responsive'>
                                                    <table className='table'>
                                                        <thead>
                                                            <tr>
                                                                {selectedColumns.map(col => <th key={col}>{formatColumnHeader(col)}</th>)}
                                                                <th>HubSpot ID</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {results.map((result, idx) => (
                                                                <tr key={idx}>
                                                                    {selectedColumns.map(col => (
                                                                        <td key={col}>{formatCellValue(result?.['data']?.[col])}</td>
                                                                    ))}
                                                                    <td>{result['hubspotId'] || '-'}</td>
                                                                    <td>
                                                                        {result['hubspotId'] &&
                                                                            <a className='open-btn' href={`https://app.hubspot.com/contacts/${migration['hubspotPortalId']}/${result['hubspotId']}`} target='_blank' rel='noreferrer'>Open in HubSpot</a>
                                                                        }
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        }
                                        {resultPages?.length > 1 &&
                                            <div className='table-paginator'>
                                                <button disabled={resultsPage === 1} className='open-btn'
                                                    onClick={() => { updateResultsPage(resultsPage - 1); fetchResults(selectedObjectName, resultsSearch, resultsPage - 1, filterType); }}>Previous</button>
                                                {getPageNumbers().map((pg, idx) => pg === '...'
                                                    ? <span key={'ellipsis-' + idx} className='paginator-ellipsis'>...</span>
                                                    : <button key={pg} disabled={pg === resultsPage} className={'open-btn ' + (pg === resultsPage ? 'black bold' : '')}
                                                        onClick={() => { updateResultsPage(pg); fetchResults(selectedObjectName, resultsSearch, pg, filterType); }}>{pg}</button>
                                                )}
                                                <button disabled={resultsPage === resultPages.length} className='open-btn'
                                                    onClick={() => { updateResultsPage(resultsPage + 1); fetchResults(selectedObjectName, resultsSearch, resultsPage + 1, filterType); }}>Next</button>
                                            </div>
                                        }
                                    </div>
                                    : <table id="fields-table" class='table-auto w-full mb-6'>
                                        <thead>
                                            <tr class='border-b border-teal'>
                                                <th>Object Name</th>
                                                <th>Records from<br />{sourceProvider}
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Total number of records currently in your {sourceProvider} instance for this object type.</p> </div>
                                                    </i>
                                                </th>
                                                <th>Records to<br />HubSpot
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Records imported into your HubSpot destination during this migration (created + updated).</p> </div>
                                                    </i>
                                                </th>
                                                <th>Extracted
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Number of records successfully retrieved from {sourceProvider} during this migration run.</p> </div>
                                                    </i>
                                                </th>
                                                <th>Created
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Number of new records created in HubSpot during this migration (record that didn't previously exist.)</p> </div>
                                                    </i>
                                                </th>
                                                <th>Updated
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Number of existing HubSpot records that were updated with new data from {sourceProvider} during this migration.</p> </div>
                                                    </i>
                                                </th>
                                                <th>Failed
                                                    <i className='icon-info'><img src={require('../../static/images/info-icon-grey.png')} alt='' />
                                                        <div className='overlabox-tooltip'> <p>Number of records that could not be migrated due to errors (e.g, validation issues, missing required fields, API limits).</p> </div>
                                                    </i>
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredObjects.length > 0 ? filteredObjects.map(object => {
                                                let stats = computeStats(object);
                                                return (
                                                    <tr key={object['key'] || object['label']}>
                                                        <td style={{ cursor: 'pointer' }}>
                                                            <span onClick={() => { setSettingsModal(true); setErrorsTabObject(null); }}>{_.capitalize(object['mapping']?.['sourceName'] || object['label'])}</span>
                                                            {stats.recordsFailed > 0 &&
                                                                <span style={{ color: 'red', fontSize: '12px', marginLeft: '8px', cursor: 'pointer' }} onClick={() => { setSettingsModal(true); setErrorsTabObject(object['key'] || object['label']); }}>
                                                                    <i className='fa fa-exclamation-triangle' style={{ marginRight: '4px' }} />
                                                                    {stats.recordsFailed} {`Error${stats.recordsFailed > 1 ? 's' : ''}`}
                                                                </span>
                                                            }
                                                        </td>
                                                        <td>{object['noOfRecord'] || object['count'] || 0}</td>
                                                        <td>{stats.recordsCreated + stats.recordsUpdated}</td>
                                                        <td onClick={() => stats.recordsExtracted > 0 && handleStatClick(null, object, 'extracted')} style={{ cursor: stats.recordsExtracted > 0 ? 'pointer' : 'default' }}>
                                                            <div className='static-precentage'>
                                                                {stats.recordsExtracted}
                                                                <span>{stats.extractedPercent}%</span>
                                                            </div>
                                                        </td>
                                                        <td onClick={() => stats.recordsCreated > 0 && handleStatClick(null, object, 'created')} style={{ cursor: stats.recordsCreated > 0 ? 'pointer' : 'default' }}>
                                                            <div className='static-precentage'>
                                                                {stats.recordsCreated}
                                                                <span>{stats.createdPercent}%</span>
                                                            </div>
                                                        </td>
                                                        <td onClick={() => stats.recordsUpdated > 0 && handleStatClick(null, object, 'updated')} style={{ cursor: stats.recordsUpdated > 0 ? 'pointer' : 'default' }}>
                                                            <div className='static-precentage'>
                                                                {stats.recordsUpdated}
                                                                <span>{stats.updatedPercent}%</span>
                                                            </div>
                                                        </td>
                                                        <td onClick={() => stats.recordsFailed > 0 && handleStatClick(null, object, 'failed')} style={{ cursor: stats.recordsFailed > 0 ? 'pointer' : 'default' }}>
                                                            <div className='static-precentage'>
                                                                {stats.recordsFailed}
                                                                <span>{stats.failedPercent}%</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan={7} style={{ textAlign: 'center' }}>No migration data available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                }

                            </div>
                            {!isRecordsExtracted &&
                                <div className='btn-area-grp' style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px 0' }}>
                                    {migration['paymentStatus'] == 'paid' && migration['status'] == 'sample' &&
                                        <button className='btn-style-one' onClick={() => dispatch(setupFullMigration(migration['key']))}> Run full migration </button>
                                    }
                                    {migration['paymentStatus'] != 'paid' &&
                                        <button className='btn-style-one' onClick={() => setPaymentModal(true)}> Purchase full migration </button>
                                    }
                                    {migration['status'] == 'in-progress' && (migration['inQueueTasks'] == 0 || !migration['inQueueTasks']) &&
                                        <button className='btn-style-one' onClick={() => dispatch(createRelations(migration['key']))}> Run Object Relations </button>
                                    }
                                    {migration['type'] === 'Soft data migration' && migration['paymentStatus'] === 'paid' && ['completed', 'in-progress', 'sample'].includes(migration['status']) &&
                                        <button className='btn-style-one' onClick={() => dispatch(resyncSoftData(migration['key']))}> Re-sync from HubSpot </button>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div >
            </VisibilitySensor>

            {/* Migration Details Modal */}
            <MigrationDetailModal
                isOpen={settingsModal}
                onClose={() => { setSettingsModal(false); setErrorsTabObject(null); }}
                initialTab={errorsTabObject ? 2 : 0}
            />

            {/* Payment Modal */}
            <Modal isOpen={paymentModal} className='modal-payment' aria-labelledby='contained-modal-title-vcenter' centered>
                <ModalBody>
                    <div className='payment-box'>
                        <Elements stripe={stripePromise} options={{ clientSecret: stripeSecret?.['secret'], appearance: {} }}>
                            <PaymentForm migrationKey={migration['key']} amount={stripeSecret?.['amount']} />
                        </Elements>
                    </div>
                </ModalBody>
            </Modal>
        </div >
    );
};


export default ReportLog;
